import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import {
  AssetImage,
  firstError,
  HeaderStats,
  unwrap,
  useHeaderSummaryShown,
  Widget,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { RESTREAMING_SERVER_BLOCKING_CONTRACT } from "./types";
import type { RestreamingServerBlockingData, RestreamingServerBlockingIsp } from "./types";

export interface RestreamingServerBlockingConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "restreaming-server-blocking",
  name: "Restreaming server blocking",
  description: "One protected event's restreaming-server (ISP) blocking outcomes, by country.",
  version: "1.0.0",
  dataRequests: {
    isps: {
      source: "protection-overview-event-hermes",
      contract: RESTREAMING_SERVER_BLOCKING_CONTRACT,
      // The dashboard instance binds this to "protection-overview-event-hermes"
      // with a report_instance_id param (see domains.dashboards.builtins). A
      // dataBindings entry only takes effect when the request declares itself
      // configurable — "false" here silently produced "Data request does not
      // allow a source override" and no data ever loaded.
      configurable: true,
    },
  },
};

export const defaultConfig: RestreamingServerBlockingConfig = {};

const percentFormat = new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 });

/** "01h56": zero-padded hours and minutes of a duration given in minutes. */
function formatDurationHM(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h.toString().padStart(2, "0")}h${m.toString().padStart(2, "0")}`;
}

type RateTone = "ok" | "warn" | "error";

/** ≥ 90 % healthy, ≥ 70 % degraded, below that failing. */
function rateTone(fraction: number): RateTone {
  if (fraction >= 0.9) return "ok";
  if (fraction >= 0.7) return "warn";
  return "error";
}

const TONE_STROKE: Record<RateTone, string> = {
  ok: "stroke-status-ok",
  warn: "stroke-status-warn",
  error: "stroke-status-error",
};
const TONE_FILL: Record<RateTone, string> = {
  ok: "bg-status-ok",
  warn: "bg-status-warn",
  error: "bg-status-error",
};
const TONE_TEXT: Record<RateTone, string> = {
  ok: "text-status-ok-text",
  warn: "text-status-warn-text",
  error: "text-status-error-text",
};

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

function groupByCountry(
  isps: RestreamingServerBlockingIsp[],
): {
  countryCode: string;
  countryName: string;
  flag?: string | null;
  isps: RestreamingServerBlockingIsp[];
}[] {
  const order: string[] = [];
  const groups = new Map<string, RestreamingServerBlockingIsp[]>();
  for (const isp of isps) {
    if (!groups.has(isp.countryCode)) {
      groups.set(isp.countryCode, []);
      order.push(isp.countryCode);
    }
    groups.get(isp.countryCode)!.push(isp);
  }
  return order.map((countryCode) => ({
    countryCode,
    countryName: groups.get(countryCode)![0].countryName,
    flag: groups.get(countryCode)![0].flag,
    isps: groups.get(countryCode)!,
  }));
}

/** Half-donut gauge of the ISP response rate, value printed in the bowl. */
function ResponseGauge({ fraction }: { fraction: number }) {
  const tone = rateTone(fraction);
  // Semicircle of radius 24 centred on (28, 28), drawn left to right.
  const arc = "M 4 28 A 24 24 0 0 1 52 28";
  return (
    <div
      className="relative h-8 w-14 shrink-0"
      role="img"
      aria-label={`Response rate ${percentFormat.format(fraction)}`}
    >
      <svg viewBox="0 0 56 32" className="h-8 w-14 overflow-visible" aria-hidden>
        <path
          d={arc}
          fill="none"
          strokeWidth={4}
          strokeLinecap="round"
          className="stroke-status-neutral-bg"
        />
        {fraction > 0 ? (
          <path
            d={arc}
            fill="none"
            strokeWidth={4}
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${fraction * 100} 100`}
            className={TONE_STROKE[tone]}
          />
        ) : null}
      </svg>
      <span
        className={`absolute inset-x-0 bottom-0.5 text-center leading-none text-2xs font-medium tabular-nums ${TONE_TEXT[tone]}`}
        aria-hidden
      >
        {percentFormat.format(fraction)}
      </span>
    </div>
  );
}

function IspItem({ isp }: { isp: RestreamingServerBlockingIsp }) {
  const rate = clamp01(isp.responseRate);
  const coverage = clamp01(isp.responseCoverageRate);
  const coverageTone = rateTone(coverage);

  return (
    <div className="flex min-w-0 items-start gap-3 rounded-lg border border-border px-3 py-2.5">
      <ResponseGauge fraction={rate} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-xs font-semibold text-primary" title={isp.name}>
          {isp.name}
        </span>
        <div className="flex items-baseline justify-between gap-2 text-2xs">
          <span className="text-muted">Coverage</span>
          <span className={`font-medium tabular-nums ${TONE_TEXT[coverageTone]}`}>
            {percentFormat.format(coverage)}
          </span>
        </div>
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-status-neutral-bg"
          role="progressbar"
          aria-label={`${isp.name} coverage`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(coverage * 100)}
        >
          <div
            className={`h-full rounded-full ${TONE_FILL[coverageTone]}`}
            style={{ width: `${coverage * 100}%` }}
          />
        </div>
        <span className="text-2xs tabular-nums text-muted" title="Coverage duration">
          {formatDurationHM(isp.coverageDurationMin)}
        </span>
      </div>
    </div>
  );
}

export function Component({ data, config }: WidgetProps<RestreamingServerBlockingConfig>) {
  const error = firstError(data, ["isps"]);
  const result = unwrap<RestreamingServerBlockingData>(data.isps);
  const loading = data.isps?.isLoading && !result;
  const heading = config.title ?? manifest.name;
  const summaryInHeader = useHeaderSummaryShown();

  return (
    <Widget
      title={heading}
      headerSummary={
        result ? (
          <HeaderStats
            label={`${heading} summary`}
            items={[
              { label: "ISPs involved", value: result.isps.length, format: "number" },
              { label: "Response rate", value: result.responseRate, format: "percent" },
              { label: "Coverage rate", value: result.responseCoverageRate, format: "percent" },
            ]}
          />
        ) : undefined
      }
    >
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="list" />
      ) : result.isps.length === 0 ? (
        <WidgetState title="No restreaming-server blocking recorded for this event." />
      ) : (
        <div className="flex flex-col gap-4 density-widget-body">
          {summaryInHeader ? null : (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-secondary">
              <span>
                {result.isps.length} ISPs · response rate{" "}
                <span className="font-semibold tabular-nums text-primary">
                  {percentFormat.format(result.responseRate)}
                </span>
              </span>
              <span>
                Coverage{" "}
                <span className="font-semibold tabular-nums text-primary">
                  {percentFormat.format(result.responseCoverageRate)}
                </span>
              </span>
            </div>
          )}
          <div className="flex flex-col divide-y divide-border">
            {groupByCountry(result.isps).map((country) => (
              <div
                key={country.countryCode}
                className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 @xl/widget:flex-row @xl/widget:gap-6"
              >
                <span className="flex w-32 shrink-0 items-center gap-2 self-start text-xs font-semibold text-primary @xl/widget:pt-3">
                  <AssetImage
                    reference={country.flag}
                    className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover"
                  />
                  <span className="truncate">{country.countryName}</span>
                </span>
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 @xl/widget:grid-cols-2 @4xl/widget:grid-cols-3 @6xl/widget:grid-cols-4">
                  {country.isps.map((isp) => (
                    <IspItem key={`${country.countryCode}:${isp.name}`} isp={isp} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<RestreamingServerBlockingConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
